import {
  AfterViewInit, ChangeDetectorRef, Component, ElementRef, EventEmitter, HostBinding, HostListener,
  Input, NgZone, OnDestroy, Output, ViewChild,
} from '@angular/core';
import {
  CERTIFICATE_FONT_CSS, CERTIFICATE_SHEET_H, CERTIFICATE_SHEET_W,
  CertificateElement, CertificateImageElement, CertificateTemplate, CertificateTextElement,
  CertificateVars, isTextElement, resolveCertificateVars, svgIntrinsicSize,
} from 'src/app/model/certificate-template';

type DragMode = 'move' | 'nw' | 'n' | 'ne' | 'e' | 'se' | 's' | 'sw' | 'w';

/**
 * Folha A4 paisagem do certificado — renderização COMPARTILHADA entre o
 * certificado do autor e o editor de template do chair.
 *
 * Canvas fixo 1123×794px + `transform: scale()` de "snap to fit" (mesma
 * técnica da presentation-certificate original). Dois conteúdos:
 *  - `[template]`: fundo → imagens (crop por moldura) → caixas de texto
 *    (variáveis resolvidas via `[vars]`);
 *  - `[statement]`: legado — enunciado único centrado (evento sem template).
 *
 * `[editable]` liga seleção + arrastar + alças de resize. O componente MUTA
 * o objeto `template` recebido (x/y/w/h) e avisa via `(changed)`; quem edita
 * o resto (texto, crop, fundo) é o painel do editor, mutando o mesmo objeto.
 * Drag/resize são feitos com pointer events manuais — CDK DragDrop não
 * coopera com ancestral `transform: scale()`.
 */
@Component({
  selector: 'app-certificate-sheet',
  templateUrl: './certificate-sheet.component.html',
  styleUrls: ['./certificate-sheet.component.scss'],
})
export class CertificateSheetComponent implements AfterViewInit, OnDestroy {
  @Input() template: CertificateTemplate | null = null;
  @Input() vars: CertificateVars | null = null;
  /** Texto legado JÁ RESOLVIDO (sem {{vars}}) — usado quando template == null. */
  @Input() statement = '';
  @Input() editable = false;
  /**
   * 'viewport': altura até o fim da janela (página do certificado);
   * 'container': cabe na caixa do host (editor, que tem painel ao lado).
   */
  @Input() fitMode: 'viewport' | 'container' = 'viewport';
  @Input() selectedId: string | null = null;
  @Output() selectedIdChange = new EventEmitter<string | null>();
  /** Disparado ao FIM de um drag/resize (p/ dirty-tracking do editor). */
  @Output() changed = new EventEmitter<void>();

  readonly sheetW = CERTIFICATE_SHEET_W;
  readonly sheetH = CERTIFICATE_SHEET_H;
  scale = 1;

  // Só o editor (`container`) recorta o canvas (crop/drag de imagem dependem
  // disso). No `viewport` o canvas fica `overflow: visible` p/ a sombra da folha
  // desvanecer no cinza em vez de ser estrangulada (ver .scss).
  @HostBinding('class.fit-container') get isFitContainer(): boolean {
    return this.fitMode === 'container';
  }

  /** Dimensões naturais por URL (p/ o crop por moldura das imagens). */
  private naturalSizes: { [url: string]: { w: number, h: number } } = {};

  @ViewChild('canvas') canvasRef: ElementRef<HTMLElement>;
  @ViewChild('toolbar') toolbarRef: ElementRef<HTMLElement>;
  private resizeObserver: ResizeObserver;

  // Estado do gesto atual (move/resize).
  private drag: {
    mode: DragMode;
    el: CertificateElement;
    startClientX: number;
    startClientY: number;
    orig: { x: number, y: number, w: number, h: number };
    moved: boolean;
  } | null = null;
  private detachPointerListeners: (() => void) | null = null;

  constructor(private cdr: ChangeDetectorRef, private zone: NgZone) {}

  ngAfterViewInit(): void {
    this.fit();
    if (typeof ResizeObserver !== 'undefined' && this.canvasRef) {
      this.resizeObserver = new ResizeObserver(() => this.zone.run(() => this.fit()));
      this.resizeObserver.observe(this.canvasRef.nativeElement);
      // Observa a barra também: quando o botão projetado renderiza/muda de tamanho,
      // re-encaixa (a reserva do stack depende da altura dela).
      if (this.toolbarRef) { this.resizeObserver.observe(this.toolbarRef.nativeElement); }
    }
  }

  ngOnDestroy(): void {
    if (this.resizeObserver) { this.resizeObserver.disconnect(); }
    if (this.detachPointerListeners) { this.detachPointerListeners(); }
  }

  @HostListener('window:resize')
  onWindowResize(): void { this.fit(); }

  /** "Snap to fit": escala a folha p/ que o STACK (barra projetada + folha) caiba
   *  e centralize na área disponível — o botão fica ancorado NA folha e o respiro
   *  acima do botão = abaixo da folha. */
  private fit(): void {
    const el = this.canvasRef && this.canvasRef.nativeElement;
    if (!el) { return; }

    // Bloco da barra projetada (botão + `margin-bottom` = gap até a folha).
    // Reservado p/ o stack caber; 0 quando não há barra (editor).
    const tb = this.toolbarRef && this.toolbarRef.nativeElement;
    const toolbarBlock = (tb && tb.offsetHeight > 0)
      ? tb.offsetHeight + (parseFloat(getComputedStyle(tb).marginBottom) || 0)
      : 0;

    let availW: number;
    let availH: number;
    if (this.fitMode === 'container') {
      // Editor: o canvas recorta (overflow:hidden) → gutter 32 p/ a sombra caber.
      availW = el.clientWidth - 2 * 32;
      availH = el.clientHeight - 2 * 32 - toolbarBlock;
    } else {
      // Viewport: fixa o canvas na área VISÍVEL (topo do canvas → fim da janela);
      // reserva o bloco da barra + um respiro ASSIMÉTRICO (topo TOP, base BOTTOM).
      // O stack (barra + folha) é pinado ao topo (align-items: flex-start + padding
      // no .scss) → TOP acima do botão, BOTTOM abaixo da folha. Os valores AQUI
      // devem casar com o padding do `.certificate-canvas`. A sombra desvanece livre
      // no cinza (shell `overflow: visible`).
      const TOP = 2;
      const BOTTOM = 20;
      const visibleH = Math.max(0, window.innerHeight - el.getBoundingClientRect().top);
      el.style.height = visibleH + 'px';
      availW = el.clientWidth;
      availH = visibleH - toolbarBlock - TOP - BOTTOM;
    }
    if (availW <= 0 || availH <= 0) { return; }
    const s = Math.min(availW / this.sheetW, availH / this.sheetH);
    if (Number.isFinite(s) && s > 0 && Math.abs(s - this.scale) > 0.001) {
      this.scale = s;
      this.cdr.detectChanges();
    }
  }

  // ------------------------------------------------------------- renderização

  resolvedText(el: CertificateTextElement): string {
    return this.vars ? resolveCertificateVars(el.content, this.vars) : el.content;
  }

  /** Stack CSS da família da caixa (espelha as fontes-padrão do PDF). */
  fontCss(el: CertificateTextElement): string {
    return CERTIFICATE_FONT_CSS[el.font] || CERTIFICATE_FONT_CSS.times;
  }

  /** Cache por elemento dos segmentos texto/variável (evita re-parse a cada CD). */
  private segmentsCache: { [id: string]: { content: string, segs: Array<{ isVar: boolean, value: string }> } } = {};

  /**
   * Modo "Variáveis" (sem vars): quebra o conteúdo em segmentos texto ×
   * {{var}} p/ o template renderizar cada variável como CHIP (.vte-chip).
   */
  rawSegments(el: CertificateTextElement): Array<{ isVar: boolean, value: string }> {
    const content = el.content || '';
    const cached = this.segmentsCache[el.id];
    if (cached && cached.content === content) { return cached.segs; }
    const segs: Array<{ isVar: boolean, value: string }> = [];
    const regex = /\{\{\s*(\w+)\s*\}\}/g;
    let last = 0;
    let m: RegExpExecArray;
    while ((m = regex.exec(content)) !== null) {
      if (m.index > last) { segs.push({ isVar: false, value: content.slice(last, m.index) }); }
      segs.push({ isVar: true, value: m[1] });
      last = m.index + m[0].length;
    }
    if (last < content.length) { segs.push({ isVar: false, value: content.slice(last) }); }
    this.segmentsCache[el.id] = { content, segs };
    return segs;
  }

  onImgLoad(url: string, ev: globalThis.Event): void {
    const img = ev.target as HTMLImageElement;
    if (!url || !img) { return; }
    if (img.naturalWidth) {
      this.naturalSizes[url] = { w: img.naturalWidth, h: img.naturalHeight };
      return;
    }
    // SVG só-viewBox: alguns browsers reportam naturalWidth 0 — busca o texto
    // e lê o viewBox (sem dimensões o crop por moldura não renderiza a img).
    fetch(url)
      .then(r => r.ok ? r.text() : null)
      .then(text => {
        const dims = text && svgIntrinsicSize(text);
        if (dims && !this.naturalSizes[url]) {
          this.naturalSizes[url] = dims;
          this.cdr.detectChanges();
        }
      })
      .catch(() => {});
  }

  /**
   * Estilo da imagem DENTRO da moldura (crop não-destrutivo): escala "cover"
   * × zoom, centrada + offsets. Mesma matemática do `cropToCanvas` do PDF.
   */
  innerImageStyle(el: CertificateImageElement): { [k: string]: string } {
    const nat = el.url && this.naturalSizes[el.url];
    if (!nat) { return { visibility: 'hidden' }; }
    const cover = Math.max(el.w / nat.w, el.h / nat.h);
    const scale = cover * (el.crop.zoom || 1);
    const drawW = nat.w * scale;
    const drawH = nat.h * scale;
    return {
      width: drawW + 'px',
      height: drawH + 'px',
      left: ((el.w - drawW) / 2 + (el.crop.offsetX || 0)) + 'px',
      top: ((el.h - drawH) / 2 + (el.crop.offsetY || 0)) + 'px',
    };
  }

  handlesFor(el: CertificateElement): DragMode[] {
    // Caixa de texto: altura é do conteúdo → só largura é redimensionável.
    return isTextElement(el)
      ? ['e', 'w']
      : ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'];
  }

  trackElement(index: number, el: CertificateElement): string { return el.id; }

  // ---------------------------------------------------------------- interação

  onSheetPointerDown(): void {
    // Clique no vazio da folha → desseleciona (o pointerdown dos elementos
    // faz stopPropagation).
    if (this.editable) { this.select(null); }
  }

  onElementPointerDown(ev: PointerEvent, el: CertificateElement, mode: DragMode): void {
    if (!this.editable) { return; }
    ev.preventDefault();
    ev.stopPropagation();
    this.select(el.id);
    this.drag = {
      mode, el,
      startClientX: ev.clientX,
      startClientY: ev.clientY,
      orig: { x: el.x, y: el.y, w: (el as any).w || 0, h: (el as any).h || 0 },
      moved: false,
    };
    // Listeners no document FORA da zona: o move só muta x/y e pede uma CD
    // local — evita CD do app inteiro a cada pixel (lição do sidebar).
    this.zone.runOutsideAngular(() => {
      const onMove = (e: PointerEvent) => this.onPointerMove(e);
      const onUp = (e: PointerEvent) => this.onPointerUp(e);
      document.addEventListener('pointermove', onMove);
      document.addEventListener('pointerup', onUp);
      this.detachPointerListeners = () => {
        document.removeEventListener('pointermove', onMove);
        document.removeEventListener('pointerup', onUp);
        this.detachPointerListeners = null;
      };
    });
  }

  private onPointerMove(ev: PointerEvent): void {
    if (!this.drag) { return; }
    // Deltas em px do CANVAS (dividir pelo scale da folha).
    const dx = (ev.clientX - this.drag.startClientX) / this.scale;
    const dy = (ev.clientY - this.drag.startClientY) / this.scale;
    if (!this.drag.moved && Math.abs(dx) < 1 && Math.abs(dy) < 1) { return; }
    this.drag.moved = true;

    const { el, mode, orig } = this.drag;
    const MIN = 20;
    if (mode === 'move') {
      // Mantém ao menos 40px do elemento dentro da folha.
      el.x = this.clamp(orig.x + dx, -((el as any).w || 200) + 40, this.sheetW - 40);
      el.y = this.clamp(orig.y + dy, -((el as any).h || 40) + 40, this.sheetH - 40);
    } else if (isTextElement(el)) {
      // Texto: só largura (alças e/w).
      if (mode === 'e') {
        el.w = this.clamp(orig.w + dx, MIN * 2, this.sheetW);
      } else if (mode === 'w') {
        const w = this.clamp(orig.w - dx, MIN * 2, this.sheetW);
        el.x = orig.x + (orig.w - w);
        el.w = w;
      }
    } else {
      const img = el as CertificateImageElement;
      if (mode.includes('e')) { img.w = this.clamp(orig.w + dx, MIN, this.sheetW); }
      if (mode.includes('s')) { img.h = this.clamp(orig.h + dy, MIN, this.sheetH); }
      if (mode.includes('w')) {
        const w = this.clamp(orig.w - dx, MIN, this.sheetW);
        img.x = orig.x + (orig.w - w);
        img.w = w;
      }
      if (mode.includes('n')) {
        const h = this.clamp(orig.h - dy, MIN, this.sheetH);
        img.y = orig.y + (orig.h - h);
        img.h = h;
      }
    }
    this.zone.run(() => this.cdr.detectChanges());
  }

  private onPointerUp(_ev: PointerEvent): void {
    const wasMoved = this.drag && this.drag.moved;
    this.drag = null;
    if (this.detachPointerListeners) { this.detachPointerListeners(); }
    if (wasMoved) { this.zone.run(() => this.changed.emit()); }
  }

  private select(id: string | null): void {
    if (this.selectedId !== id) {
      this.selectedId = id;
      this.selectedIdChange.emit(id);
    }
  }

  private clamp(v: number, min: number, max: number): number {
    return Math.min(max, Math.max(min, v));
  }
}
